import type { RootState } from 'app/reducers/index.ts';
import type { UserDto } from '@/api-gen/api.schemas';
import { UserRole } from '@/api-gen/api.schemas';
import { AccessLevel } from '@/api-gen/api.schemas';
import type { LoadingStatus } from 'app/store/types.ts';

export const CurrentUserSelector: (state: RootState) => UserDto = (state) => state.userReducer.currentUser;

export const CurrentUserLoadingStatusSelector: (state: RootState) => LoadingStatus = (state) =>
  state.userReducer.currentUserLoadingStatus;

export const CurrentUserNotFoundSelector: (state: RootState) => boolean = (state) =>
  state.userReducer.currentUserNotFound;

export const CurrentUserLoadErrorSelector = (state: RootState) => state.userReducer.currentUserLoadError;

export const RoleSelector: (state: RootState) => UserRole = (state) => state.userReducer.currentUser?.role as UserRole;

const createHasRoleSelector =
  (role: UserRole) =>
  (state: RootState): boolean =>
    RoleSelector(state) === role;

export const isUserSuperAdminSelector = createHasRoleSelector(UserRole.SuperAdmin);

export const isUserOwnerSelector = createHasRoleSelector(UserRole.Owner);

export const isUserAdminSelector = createHasRoleSelector(UserRole.Admin);

export const isUserMemberSelector = createHasRoleSelector(UserRole.Member);

export const isUserAnyAdminSelector: (state: RootState) => boolean = (state) =>
  isUserSuperAdminSelector(state) || isUserOwnerSelector(state) || isUserAdminSelector(state);

export const userCompanyIdSelector: (state: RootState) => number | undefined = (state) =>
  state.userReducer.currentUser?.companyId;

// Helper function to get user's organization access level for a specific protocol
export const getUserOrganizationAccessLevel = (
  protocol: { companies?: Array<{ companyId: number; access?: AccessLevel }> },
  userCompanyId?: number
): AccessLevel | null => {
  if (!protocol.companies || !userCompanyId) return null;

  const userOrgAccess = protocol.companies.find((pc) => pc.companyId === userCompanyId);
  return userOrgAccess?.access || null;
};

// Selector to determine if current user can edit evaluation protocols
export const canUserEditEvaluationProtocolSelector =
  (protocol: { companies?: Array<{ companyId: number; access?: AccessLevel }> }) =>
  (state: RootState): boolean => {
    const userRole = RoleSelector(state);
    const userCompanyId = userCompanyIdSelector(state);

    // SuperAdmins can always edit
    if (userRole === UserRole.SuperAdmin) return true;

    // For Admin/Owner users, check their organization's access level
    if (userRole === UserRole.Admin || userRole === UserRole.Owner) {
      const userOrgAccess = getUserOrganizationAccessLevel(protocol, userCompanyId);
      return userOrgAccess === AccessLevel.CanEdit || userOrgAccess === AccessLevel.CanAdministrate;
    }

    // Members cannot edit regardless of access level
    return false;
  };

// Per-session runtime override so E2E tests / manual QA can exercise the legacy VAD path
// without being in a hardcoded company/department list. Honored in ALL environments
// (dev and prod alike) and only affects the session whose URL carries the query param.
const isLegacyVadForcedByOverride = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('legacyVad') === '1';
  } catch {
    return false;
  }
};

// Function to determine if a user should use legacy VAD based on company and department IDs
export const shouldUseLegacyVadSelector = (state: RootState): boolean => {
  const user = CurrentUserSelector(state);

  // Check if we're in development environment
  const isDev = process.env.NODE_ENV === 'development';

  // Runtime override: `?legacyVad=1` forces legacy VAD for that session only,
  // in any environment. Intended for testing the legacy VAD path.
  if (isLegacyVadForcedByOverride()) return true;

  if (!user || !user.companyId) return false;

  // List of companies that should use legacy VAD in production
  const prodLegacyVadCompanyIds = [];

  // Department-specific overrides for production
  const prodLegacyVadDepartmentIds = [45]; // Bristol Myers Squibb (department under NECI)

  // Different list for development environment
  const devLegacyVadCompanyIds = [
    //1, // Sklls
  ];

  // Department-specific overrides for development
  const devLegacyVadDepartmentIds = [];

  // Use appropriate lists based on environment
  const legacyVadCompanyIds = isDev ? devLegacyVadCompanyIds : prodLegacyVadCompanyIds;
  const legacyVadDepartmentIds = isDev ? devLegacyVadDepartmentIds : prodLegacyVadDepartmentIds;

  return (
    legacyVadCompanyIds.includes(user.companyId) ||
    (user.departmentId !== undefined && legacyVadDepartmentIds.includes(user.departmentId))
  );
};
